import { useState, useEffect, useCallback } from 'react';
import { getCorrectionStatus } from '../services/api';

// 類型定義
export interface CorrectionTicket {
  ticket: string;
  status: string;
  studentID: string;
  chapter: string;
  _id: string;
  createdAt: string;
  modifiedAt: string | null;
  // 後端建票時一併存的班級碼與書（composition-api service/ticket.py）；舊票可能沒有
  classCode?: string;
  bookID?: string;
}

interface UseCorrectionStatusOptions {
  studentId: string | null;
  pollInterval?: number;
  onCompleted?: (completedTickets: string[]) => void;
  onFailed?: (failedTickets: CorrectionTicket[]) => void;
  enablePolling?: boolean;
}

interface UseCorrectionStatusReturn {
  correctionTickets: CorrectionTicket[];
  failedTickets: CorrectionTicket[];
  recentlyCompletedTickets: string[];
  isProcessing: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  resetStatus: () => void;
}

// 失敗狀態判斷（kanban 1000412）：
// 後端（composition-api main.py）所有失敗狀態一律以「失敗.」開頭
// （失敗. 沒有匹配的prompt／失敗. 請輸入需要批改的文字或圖片／
//   失敗. AI無法辨識此圖片內容(0)／失敗. 批改結果格式錯誤…），
// 其餘狀態只有「準備中」「圖片辨識中」「批改中」「已完成」（kanban 1000823 補上「準備中」）。
// 原本用精確清單比對，漏接「失敗. AI無法辨識此圖片內容0」與新增的
// 「失敗. 批改結果格式錯誤」，導致該 ticket 從學生端無聲消失——
// 改用前綴判斷，後端新增失敗字串也不會再漏。
export const isFailureStatus = (status: string): boolean =>
  status.startsWith('失敗');

// 處理中狀態判斷（kanban 1000823）：
// 後端建票的初始狀態是「準備中」（composition-api service/ticket.py），要等圖片上 S3、
// 稿紙校正做完才變「圖片辨識中」，之後才是「批改中」。學生送出後回到列表那次查詢
// 多半落在「準備中」；原本只認後兩種，把「準備中」當成沒有處理中的票 → 不啟動輪詢
// → 列表永遠不出現「批改中」列，要手動重新整理才看得到。
const PROCESSING_STATUSES = ['準備中', '圖片辨識中', '批改中'];

export const isProcessingStatus = (status: string): boolean =>
  PROCESSING_STATUSES.includes(status);

// 把一批票分成處理中／失敗／已完成（純函式，方便測試）
export const partitionTickets = (tickets: CorrectionTicket[]) => ({
  processing: tickets.filter((ticket) => isProcessingStatus(ticket.status)),
  failed: tickets.filter((ticket) => isFailureStatus(ticket.status)),
  completed: tickets
    .filter((ticket) => ticket.status === '已完成')
    .map((ticket) => ticket.ticket),
});

// 列表頁要顯示哪些票（kanban 1000823）：
// 歷屆學測／歷屆模考沿用陪練國寫的班級碼，只比班級碼會讓同一張票在兩本書底下都出現
// 「批改中」，完成後的紀錄卻按書撈、只在上傳那本書出現。票上有 bookID 就一併比對；
// 舊票缺欄位的維持原本「有什麼比什麼」的行為，不讓它消失。
export const selectTicketsForView = (
  tickets: CorrectionTicket[],
  view: { classCode?: string; bookId?: string }
): CorrectionTicket[] =>
  tickets.filter((ticket) => {
    if (ticket.classCode && ticket.classCode !== view.classCode) return false;
    if (ticket.bookID && view.bookId && ticket.bookID !== view.bookId) return false;
    return true;
  });

export const useCorrectionStatus = ({
  studentId,
  pollInterval = 20000,
  onCompleted,
  onFailed,
  enablePolling = true
}: UseCorrectionStatusOptions): UseCorrectionStatusReturn => {
  const [correctionTickets, setCorrectionTickets] = useState<CorrectionTicket[]>([]);
  const [failedTickets, setFailedTickets] = useState<CorrectionTicket[]>([]);
  const [recentlyCompletedTickets, setRecentlyCompletedTickets] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);
  
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);

  // 輪詢批改狀態
  const pollCorrectionStatus = useCallback(async (isInitialCheck = false): Promise<boolean> => {
    if (!studentId) return false;

    try {
      const response = await getCorrectionStatus(studentId);
      
      if (response.data && Array.isArray(response.data)) {
        const allTickets = response.data;
        
        // 篩選不同狀態的票據
        const {
          processing: processingTickets,
          failed: failedTicketsList,
          completed: completedTickets,
        } = partitionTickets(allTickets as CorrectionTicket[]);
        
        const hasProcessingTickets = processingTickets.length > 0;
        
        // 初始檢查：如果沒有批改中的票據，不啟動輪詢
        if (isInitialCheck && !hasProcessingTickets) {
          setCorrectionTickets([]);
          setFailedTickets(failedTicketsList);
          setRecentlyCompletedTickets(completedTickets);
          setIsProcessing(false);
          return false;
        }
        
        // 設置狀態
        setCorrectionTickets(processingTickets);
        setFailedTickets(failedTicketsList);
        setRecentlyCompletedTickets(completedTickets);
        setIsProcessing(hasProcessingTickets);
        
        // 如果沒有批改中的票據，停止輪詢
        if (!hasProcessingTickets) {
          // 觸發完成回調
          if (completedTickets.length > 0 && onCompleted) {
            onCompleted(completedTickets);
          }
          
          // 觸發失敗回調
          if (failedTicketsList.length > 0 && onFailed) {
            onFailed(failedTicketsList);
          }
          
          return false;
        }
        return true; // 繼續輪詢
      } else {
        // API 回應為空，可能所有批改都完成了
        if (correctionTickets.length > 0) {
          const allCompletedTickets = correctionTickets.map(t => t.ticket);
          setRecentlyCompletedTickets(prev => [...prev, ...allCompletedTickets]);
          
          if (onCompleted) {
            onCompleted(allCompletedTickets);
          }
          
          // 3秒後移除高亮
          setTimeout(() => {
            setRecentlyCompletedTickets(prev => 
              prev.filter(id => !allCompletedTickets.includes(id))
            );
          }, 3000);
        }
        
        setCorrectionTickets([]);
        setIsProcessing(false);
        return false;
      }
    } catch (error) {
      console.error('❌ 查詢批改狀態失敗:', error);
      return true; // 發生錯誤時繼續輪詢，可能是網路問題
    }
  }, [studentId, onCompleted, onFailed, correctionTickets]);

  // 開始輪詢
  const startPolling = useCallback(() => {
    if (!enablePolling || !studentId) {
      return;
    }

    // 設置20秒後自動停止輪詢
    const timeoutId = setTimeout(() => {
      stopPolling();
    }, 20000);
    setPollingTimeout(timeoutId);

    // 立即執行一次初始檢查
    pollCorrectionStatus(true).then((shouldContinuePolling) => {
      if (shouldContinuePolling) {
        // 只有當需要繼續輪詢時才啟動 interval
        const intervalId = setInterval(async () => {
          const continuePolling = await pollCorrectionStatus(false);
          if (!continuePolling) {
            clearInterval(intervalId);
            setPollIntervalId(null);
            if (pollingTimeout) {
              clearTimeout(pollingTimeout);
              setPollingTimeout(null);
            }
          }
        }, pollInterval);
        
        setPollIntervalId(intervalId);
      }
    });
  }, [enablePolling, studentId, pollCorrectionStatus, pollInterval]);

  // 停止輪詢
  const stopPolling = useCallback(() => {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      setPollIntervalId(null);
    }
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      setPollingTimeout(null);
    }
  }, [pollIntervalId, pollingTimeout]);

  // 重置狀態
  const resetStatus = useCallback(() => {
    setCorrectionTickets([]);
    setFailedTickets([]);
    setRecentlyCompletedTickets([]);
    setIsProcessing(false);
    stopPolling();
  }, [stopPolling]);

  // 自動啟動輪詢 (只在 enablePolling 或 studentId 改變時執行)
  useEffect(() => {
    if (enablePolling && studentId) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enablePolling, studentId]); // 只依賴這兩個穩定的值

  // 單獨處理 recordStatusUpdate 事件監聽
  useEffect(() => {
    const handleRecordUpdate = () => {
      pollCorrectionStatus(false).then((shouldRestart) => {
        if (shouldRestart && !pollIntervalId) {
          startPolling();
        }
      });
    };

    window.addEventListener('recordStatusUpdate', handleRecordUpdate);

    return () => {
      window.removeEventListener('recordStatusUpdate', handleRecordUpdate);
    };
  }, [pollCorrectionStatus, pollIntervalId, startPolling]); // 明確列出依賴

  return {
    correctionTickets,
    failedTickets,
    recentlyCompletedTickets,
    isProcessing,
    startPolling,
    stopPolling,
    resetStatus
  };
};